import { CircleAlert, CircleCheck, Save } from 'lucide-react'
import { useState } from 'react'

import {
  errorView,
  usePhotoSettingsQuery,
  usePhotoSettingsSaveMutation,
  type ErrorView,
  type PhotoSettings,
  type PhotoSettingsUpdate,
} from '@krp/client-runtime'
import { Button, ErrorNotice, TextField } from '@krp/ui-kit'

import '../../app/photoSettings.css'

const MIB = 1024 * 1024

/** Целое число из поля; пусто или не число — `null`. */
function wholeNumber(text: string): number | null {
  const trimmed = text.trim()
  return /^\d{1,12}$/.test(trimmed) ? Number(trimmed) : null
}

/** Что не так с введённым числом, словами; `null` — всё хорошо. */
function problemOf(value: number | null, min: number, max: number, unit: string): string | null {
  if (value === null) return 'Введите целое число.'
  if (value < min || value > max) return `Допустимо от ${min} до ${max} ${unit}.`
  return null
}

const timeOf = (iso: string | null): string => {
  if (iso === null) return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export interface PhotoSettingsPanelProps {
  settings: PhotoSettings | null
  /** Настройки ещё не пришли. */
  loading: boolean
  /** Настройки не прочитаны: ошибка сервера дословно. */
  loadError: ErrorView | null
  saving: boolean
  /** Сохранение не прошло: ошибка сервера дословно (в том числе «настройки уже изменены другим человеком»). */
  saveError: ErrorView | null
  /** Последнее сохранение прошло. */
  saved: boolean
  onSave: (update: PhotoSettingsUpdate) => void
  /** Перечитать настройки (после отказа по версии или сбоя чтения). */
  onReload: () => void
}

/**
 * Настройки раздела «Фото» (RP-CACHE-0), окно в «Настройках»: сколько суток живёт загруженное фото до отправки в источник, через сколько суток без обращения
 * удаляются кадры поиска и сколько места они могут занимать. Допустимые пределы присылает сервер; он же решает, подходят ли числа. Число, которое человек не менял,
 * уходит на сервер таким, каким пришло.
 */
export function PhotoSettingsPanel({ settings, loading, loadError, saving, saveError, saved, onSave, onReload }: PhotoSettingsPanelProps) {
  if (loadError !== null) {
    return (
      <section className="photo-settings" aria-label="Настройки фото">
        <ErrorNotice error={loadError} title="Настройки фото не прочитаны" action={<Button onClick={onReload} actionId="platform.photo-settings.reload">Перечитать</Button>} />
      </section>
    )
  }
  if (settings === null || loading) {
    return (
      <section className="photo-settings" aria-label="Настройки фото">
        <p role="status" className="photo-settings__state">
          Читаем настройки…
        </p>
      </section>
    )
  }
  // key по версии: после сохранения или перечитывания поля заполняются заново тем, что на сервере.
  return <PhotoSettingsForm key={settings.version} settings={settings} saving={saving} saveError={saveError} saved={saved} onSave={onSave} onReload={onReload} />
}

interface FormProps {
  settings: PhotoSettings
  saving: boolean
  saveError: ErrorView | null
  saved: boolean
  onSave: (update: PhotoSettingsUpdate) => void
  onReload: () => void
}

function PhotoSettingsForm({ settings, saving, saveError, saved, onSave, onReload }: FormProps) {
  const shownMib = String(Math.round(settings.framesMaxBytes / MIB))
  const [staging, setStaging] = useState(String(settings.stagingLifetimeDays))
  const [frames, setFrames] = useState(String(settings.framesTtlDays))
  const [mib, setMib] = useState(shownMib)
  const { ranges } = settings
  const minMib = Math.ceil(ranges.minFramesMaxBytes / MIB)
  const maxMib = Math.floor(ranges.maxFramesMaxBytes / MIB)

  const stagingValue = wholeNumber(staging)
  const framesValue = wholeNumber(frames)
  const mibValue = wholeNumber(mib)
  const problems = {
    staging: problemOf(stagingValue, ranges.minStagingLifetimeDays, ranges.maxStagingLifetimeDays, 'суток'),
    frames: problemOf(framesValue, ranges.minFramesTtlDays, ranges.maxFramesTtlDays, 'суток'),
    mib: problemOf(mibValue, minMib, maxMib, 'МиБ'),
  }
  const hasProblem = problems.staging !== null || problems.frames !== null || problems.mib !== null
  const dirty = staging !== String(settings.stagingLifetimeDays) || frames !== String(settings.framesTtlDays) || mib !== shownMib

  const save = () => {
    if (stagingValue === null || framesValue === null || mibValue === null) return
    onSave({
      stagingLifetimeDays: stagingValue,
      framesTtlDays: framesValue,
      // Потолок, которого человек не менял, уходит байт в байт — округление до МиБ его не портит.
      framesMaxBytes: mib === shownMib ? settings.framesMaxBytes : mibValue * MIB,
      version: settings.version,
    })
  }

  const edited =
    settings.updatedAt === null
      ? 'Настройки ещё не сохранялись: действуют значения по умолчанию.'
      : `Последняя правка: ${timeOf(settings.updatedAt)}${settings.updatedBy === null ? '' : `, ${settings.updatedBy}`}.`

  return (
    <section className="photo-settings" aria-label="Настройки фото">
      <p className="photo-settings__state">{edited}</p>

      <div className="photo-settings__group">
        <TextField label="Срок временной загрузки, суток" value={staging} onChange={setStaging} />
        <p className="photo-settings__hint">
          Сколько суток живёт загруженное фото, пока его не отправили в источник. Новый срок действует на фото, загруженные после сохранения.
        </p>
        {problems.staging !== null && <p className="photo-settings__problem">{problems.staging}</p>}
      </div>

      <div className="photo-settings__group">
        <TextField label="Срок хранения кадров поиска, суток" value={frames} onChange={setFrames} />
        <p className="photo-settings__hint">Через сколько суток без обращения кадр поиска удаляется из кэша; при необходимости он строится заново.</p>
        {problems.frames !== null && <p className="photo-settings__problem">{problems.frames}</p>}
      </div>

      <div className="photo-settings__group">
        <TextField label="Потолок размера кадров поиска, МиБ" value={mib} onChange={setMib} />
        <p className="photo-settings__hint">Сколько места могут занимать все кадры поиска; при превышении удаляются самые давние по обращению.</p>
        {problems.mib !== null && <p className="photo-settings__problem">{problems.mib}</p>}
      </div>

      <Button
        variant="primary"
        busy={saving}
        icon={<Save size={16} aria-hidden="true" />}
        disabled={!dirty || hasProblem}
        disabledReason={hasProblem ? 'Исправьте отмеченные числа.' : !dirty ? 'Ничего не изменено.' : null}
        onClick={save}
        actionId="platform.photo-settings.save"
      >
        Сохранить
      </Button>

      {saveError !== null && <ErrorNotice error={saveError} title="Настройки не сохранены" action={<Button onClick={onReload} actionId="platform.photo-settings.reload">Перечитать</Button>} />}
      {saved && saveError === null && !dirty && (
        <p role="status" className="photo-settings__state">
          <CircleCheck size={16} aria-hidden="true" /> Сохранено.
        </p>
      )}
      {hasProblem && (
        <p className="photo-settings__hint">
          <CircleAlert size={14} aria-hidden="true" /> Пока числа неверны, сохранить нельзя.
        </p>
      )}
    </section>
  )
}

/** Окно «Фото» в «Настройках»: настройки читаются с сервера, сохранение идёт поверх увиденной версии. */
export function PhotoSettingsBinding() {
  const query = usePhotoSettingsQuery(undefined, { refetchOnMountOrArgChange: true })
  const [save, saved] = usePhotoSettingsSaveMutation()
  return (
    <PhotoSettingsPanel
      settings={query.data ?? null}
      loading={query.isLoading}
      loadError={query.isError ? errorView(query.error) : null}
      saving={saved.isLoading}
      saveError={saved.isError ? errorView(saved.error) : null}
      saved={saved.isSuccess}
      onSave={(update) => void save(update)}
      onReload={() => {
        saved.reset()
        void query.refetch()
      }}
    />
  )
}
